import { css } from 'styled-components';
import SVGInline from 'components/SVGInline/SVGInline';
import { styled } from '../ThemeConfigConnector/ThemeConfigConnnector';
import { Breakpoints, MQ, MQ_NOT_TOUCH } from '../../style/styled-components/cssMediaQueries';
import { fontBold, fontNormal, headerZIndex } from '../../style/styled-components/cssGlobalVariables';
import { hexToRgba } from '../../utils/fnColors';
import { ellipsis } from '../../style/styled-components/reusable.css';
import { MainMenu } from './MainMenu';
import { buttonReset } from '../../style/styled-components/cssButtonReset';

const logoWidth = '37px';

export const Logo = styled.a<{ imageSrc: string }>`
    display: block;
    cursor: pointer;
    float: left;
    background-image: url(${(props) => props.imageSrc});
    background-repeat: no-repeat;
    background-size: cover;
    opacity: 1;
    transition: all 0.2s ease;
    justify-self: center;
    width: ${logoWidth};
    height: 32px;
    margin: 3px 0;
`;

export const StyledSVGInline = styled(SVGInline)`
    height: 15px;
    padding-right: 24px;

    svg {
        height: 100%;
        width: auto;
    }
`;

export const GlobalNavPageTitle = styled.div`
    ${ellipsis(1)};
    font-size: 20px;
    line-height: 24px;
`;

export const GlobalBackNavigation = styled.button`
    ${buttonReset}
    display: flex;
    font-family: ${fontBold};
    color: ${(props) => props.theme.brandedConfig.high_contrast};
    cursor: pointer;
    align-items: center;
    margin: 0;

    &:focus-visible {
        border-bottom: var(--focus-outline-solid);
        outline: none;
    }

    ${MQ(Breakpoints.s)} {
        padding: 12px 0 4px 0;
        padding-left: 8px;
    }

    ${MQ(Breakpoints.m)} {
        width: fit-content;
        padding-left: 0;
        clear: both;

        ${StyledSVGInline} {
            display: flex;
            align-items: center;
            justify-content: center;
            width: ${logoWidth};
            padding-right: 0;
            margin-right: 8px;
        }
    }
`;

export const HeaderWrapper = styled.header<{ isSticky: boolean; hideOnMobile: boolean; hasSubpageBackNavigation: boolean }>`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    height: auto;
    z-index: ${headerZIndex};
    transition: all 0.2s ease-in;
    opacity: 1;
    padding: 8px 16px;
    min-height: 48px;
    background-color: ${(props) => (props.isSticky ? props.theme.brandedConfig.background : 'transparent')};
    border-bottom: 1px solid ${(props) => (props.hasSubpageBackNavigation ? 'var(--color-neutral-dark-body-10)' : 'transparent')};

    ${MQ(Breakpoints.s)} {
        padding: ${(props) => (props.hideOnMobile ? '0' : '8px 32px')};
    }

    ${MQ(Breakpoints.m)} {
        padding: 8px 72px;
    }

    &::before {
        height: 80px;
        content: ' ';
        position: absolute;
        left: 0;
        top: 0;
        right: 0;
        bottom: 0;
        opacity: 0.9;
        z-index: -1;
        background: linear-gradient(
            0deg,
            ${(props) => props.theme.brandedConfig.background} 0%,
            ${(props) => hexToRgba(props.theme.brandedConfig.background, 0)} 100%
        );
        display: flex;
        flex-direction: row-reverse;
        justify-content: space-between;
        transform: rotate(-180deg);
        transition: opacity 0.3s ease-out;
        pointer-events: none;
    }

    &.with-back-navigation {
        display: flex;
        flex-direction: row-reverse;
        justify-content: space-between;

        ${Logo} {
            display: none;
        }

        ${MQ(Breakpoints.m)} {
            display: block;
            flex-direction: initial;
            justify-content: initial;

            ${Logo} {
                display: block;
            }
        }
    }

    .menu-wrapper {
        display: flex;
        align-items: center;
        justify-content: flex-end;

        ${MQ(Breakpoints.m)} {
            justify-content: space-between;
        }
    }

    ${(props) =>
        props.hideOnMobile
            ? css`
                  height: 0;
                  padding: 0;
                  min-height: 0;

                  &:before {
                      display: none;
                  }

                  & > * {
                      display: none;
                  }

                  ${MainMenu} {
                      display: block;
                  }

                  ${MQ(Breakpoints.m)} {
                      height: auto;

                      & > * {
                          display: block;
                      }

                      &:before {
                          display: block;
                      }
                  }
              `
            : ``}
`;

export const HeaderButtonsWrapper = styled.div<{ hideOnMobile: boolean }>`
    display: flex;
    align-items: center;
    gap: 8px;

    ${MQ(Breakpoints.xs)} {
        margin-left: 40px;
    }

    ${(props) =>
        props.hideOnMobile
            ? css`
                  display: none;

                  ${MQ(Breakpoints.m)} {
                      display: flex;
                      justify-content: flex-end;
                  }
              `
            : ``}
`;

export const LoginButton = styled.button`
    ${buttonReset}
    cursor: pointer;
    font-size: 16px;
    line-height: 24px;

    &:focus-visible {
        border-bottom: 2px solid #eee;
        margin-bottom: -2px;
        outline: none;
    }

    ${MQ_NOT_TOUCH} {
        &:hover {
            opacity: 0.6;
        }
    }
`;

export const HeaderCountWrapper = styled(GlobalNavPageTitle)`
    padding-left: 8px;
    color: ${(props) => hexToRgba(props.theme.brandedConfig.white_color, 0.7)};
    font-family: ${fontNormal};
`;
